import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconBack, IconCheck, IconRestore } from '../../components/icons';
import { useAuth } from '../../auth/useAuth';
import { useConfig } from '../../context/ConfigContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { formatMoney } from '../../utils/format';

interface BankAccount {
  id: number;
  name: string;
  chart_of_account: { code: string; name: string };
}

interface StatementLine {
  id: number;
  transaction_date: string;
  description: string;
  reference: string | null;
  amount: string;
  status: 'unmatched' | 'matched' | 'cleared';
  matchable_type: string | null;
  matched_by: { id: number; full_name: string } | null;
}

const STATUS_LABEL: Record<string, string> = { unmatched: 'Unmatched', matched: 'Matched', cleared: 'Cleared' };

const MATCHABLE_LABEL: Record<string, string> = {
  'App\\Models\\BankDeposit': 'Deposit Slip',
  'App\\Models\\GlBatchLine': 'GL Batch Line',
  'App\\Models\\JournalEntryLine': 'Journal Entry Line',
};

export function BankAccountStatementLines() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const { settings } = useConfig();
  const confirmAction = useConfirm();
  const { showError, showSuccess } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [bankAccount, setBankAccount] = useState<BankAccount | null>(null);
  const [lines, setLines] = useState<StatementLine[] | null>(null);
  const [status, setStatus] = useState('');
  const [importing, setImporting] = useState(false);
  const [matching, setMatching] = useState(false);

  const canImport = can('banking.create');
  const canConfigure = can('banking.configure');
  const fmtMoney = (n: string) => formatMoney(Number(n), settings.currency);

  const load = () => {
    api.get<BankAccount>(`/bank-accounts/${id}`).then((res) => setBankAccount(res.data));
    api
      .get<StatementLine[]>(`/bank-accounts/${id}/statement-lines`, { params: { status: status || undefined } })
      .then((res) => setLines(res.data));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, status]);

  const onImportFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post<{ imported: number }>(`/bank-accounts/${id}/statement-lines/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showSuccess(`Imported ${res.data.imported} statement line(s).`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const autoMatch = async () => {
    setMatching(true);
    try {
      const res = await api.post<{ matched: number; unmatched: number }>(`/bank-accounts/${id}/statement-lines/auto-match`);
      showSuccess(`Matched ${res.data.matched} line(s); ${res.data.unmatched} still unmatched.`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Auto-match failed.');
    } finally {
      setMatching(false);
    }
  };

  const unmatch = async (line: StatementLine) => {
    if (!(await confirmAction('Undo this match?', { confirmLabel: 'Unmatch' }))) return;
    try {
      await api.post(`/bank-statement-lines/${line.id}/unmatch`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const clear = async (line: StatementLine) => {
    if (!(await confirmAction('Clear this matched line? This confirms the match has been reviewed.', { confirmLabel: 'Clear' }))) return;
    try {
      await api.post(`/bank-statement-lines/${line.id}/clear`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'You may not clear a line you matched yourself.');
    }
  };

  const reopen = async (line: StatementLine) => {
    if (!(await confirmAction('Reopen this cleared line back to matched?', { confirmLabel: 'Reopen' }))) return;
    try {
      await api.post(`/bank-statement-lines/${line.id}/reopen`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  if (!bankAccount) return null;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to bank accounts" to="/banking/accounts" />
          <h1>{bankAccount.name} — Statement Lines</h1>
        </div>
        <div className="page-header-actions">
          {canImport && (
            <>
              <input ref={fileInputRef} type="file" accept=".csv,.txt" hidden onChange={onImportFile} />
              <button type="button" className="btn btn-ghost" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                {importing ? 'Importing…' : 'Import CSV'}
              </button>
              <button type="button" className="btn btn-primary" onClick={autoMatch} disabled={matching}>
                {matching ? 'Matching…' : 'Auto-Match'}
              </button>
            </>
          )}
        </div>
      </header>
      <p className="muted">
        Linked to {bankAccount.chart_of_account.code} — {bankAccount.chart_of_account.name}. CSV columns: date, description, amount
        (positive for money in, negative for money out), reference (optional).
      </p>

      <div className="toolbar">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="unmatched">Unmatched</option>
          <option value="matched">Matched</option>
          <option value="cleared">Cleared</option>
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Reference</th>
                <th className="right">Amount</th>
                <th>Status</th>
                <th>Matched Against</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines?.map((line) => (
                <tr key={line.id}>
                  <td>{line.transaction_date}</td>
                  <td>{line.description}</td>
                  <td className="muted">{line.reference ?? '—'}</td>
                  <td className="right">{fmtMoney(line.amount)}</td>
                  <td>
                    <span className={`badge ${line.status === 'cleared' ? 'badge-active' : line.status === 'matched' ? 'badge-neutral' : 'badge-important'}`}>
                      {STATUS_LABEL[line.status]}
                    </span>
                  </td>
                  <td className="muted">{line.matchable_type ? MATCHABLE_LABEL[line.matchable_type] ?? line.matchable_type : '—'}</td>
                  <td className="right">
                    {line.status === 'matched' && (
                      <>
                        <IconButton icon={IconRestore} label="Unmatch" onClick={() => unmatch(line)} />
                        {canConfigure && <IconButton icon={IconCheck} label="Clear" onClick={() => clear(line)} />}
                      </>
                    )}
                    {line.status === 'cleared' && canConfigure && (
                      <IconButton icon={IconRestore} label="Reopen" onClick={() => reopen(line)} />
                    )}
                  </td>
                </tr>
              ))}
              {lines?.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted center">
                    No statement lines found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
